import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Check, MessageSquare, Paperclip, Send, X } from 'react-feather';
import {
  adjuntoDentroDeLimite,
  tamanoLegible,
  useRespuestas,
  useTicketBorrador,
  useGuardarBorrador,
  useDescartarBorrador,
} from '../../features/tickets';
import TicketCcEditor from './TicketCcEditor';
import RespuestaEditor from './RespuestaEditor';

const MAX_BORRADOR = 50000;
const SIN_ESTADOS = [];
const SIN_CC = [];

// Estados finales con su atajo propio en el menú del botón Enviar (Cerrado, Resuelto).
const ESTADO_CERRADO = 6;
const ESTADO_RESUELTO = 5;

const etiquetaEnvio = (est) => {
  if (Number(est.id) === ESTADO_CERRADO) return 'Enviar y cerrar';
  if (Number(est.id) === ESTADO_RESUELTO) return 'Enviar y resolver';
  return `Enviar y pasar a «${est.descripcion}»`;
};

// Cerrado y Resuelto primero: son los que se usan a diario.
const prioridadEnvio = (est) => {
  if (Number(est.id) === ESTADO_CERRADO) return 0;
  if (Number(est.id) === ESTADO_RESUELTO) return 1;
  return 2;
};

const TicketComposer = ({
  estadoActualId = null,
  estados = SIN_ESTADOS,
  onSend,
  enviando = false,
  headquarter = '',
  ticketId = null,
  solicitante = '',
  cc = SIN_CC,
  onChangeCc = null,
  ccGuardando = false,
}) => {
  const [modo, setModo] = useState('publica');
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [error, setError] = useState('');
  const [macrosAbierto, setMacrosAbierto] = useState(false);
  const [envioAbierto, setEnvioAbierto] = useState(false);
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const macrosRef = useRef(null);
  const envioRef = useRef(null);

  const { data: respuestas = [] } = useRespuestas(headquarter);

  // Borrador del agente (autosave por ticket). Un unico borrador por ticket: se
  // guarda lo que haya en el textarea sin distinguir modo (publica/nota).
  const { data: borradorInicial } = useTicketBorrador(headquarter, ticketId);
  const { mutate: guardarBorrador } = useGuardarBorrador(headquarter, ticketId);
  const { mutate: descartarBorrador } = useDescartarBorrador(headquarter, ticketId);
  const borradorAplicado = useRef(false);
  const ultimoGuardado = useRef('');

  // Restaurar el borrador al abrir el ticket, una sola vez, sin pisar lo que el
  // agente ya haya empezado a escribir.
  useEffect(() => {
    if (borradorAplicado.current || borradorInicial === undefined) return;
    borradorAplicado.current = true;
    ultimoGuardado.current = borradorInicial || '';
    if (borradorInicial) {
      setTexto(borradorInicial);
      editorRef.current?.fijarTexto(borradorInicial);
    }
  }, [borradorInicial]);

  // Autoguardado con debounce: al parar de escribir se hace upsert del borrador,
  // o se descarta si el textarea queda vacio. No dispara hasta haber restaurado.
  useEffect(() => {
    if (!borradorAplicado.current || texto === ultimoGuardado.current) return undefined;
    const handle = setTimeout(() => {
      ultimoGuardado.current = texto;
      const limpio = texto.trim();
      if (limpio.length === 0) descartarBorrador();
      else guardarBorrador(limpio.slice(0, MAX_BORRADOR));
    }, 1000);
    return () => clearTimeout(handle);
  }, [texto, guardarBorrador, descartarBorrador]);

  // Cerrar el popover de respuestas al hacer click fuera o pulsar Escape.
  useEffect(() => {
    if (!macrosAbierto) return undefined;
    const onClickFuera = (e) => {
      if (macrosRef.current && !macrosRef.current.contains(e.target)) setMacrosAbierto(false);
    };
    const onEscape = (e) => {
      if (e.key === 'Escape') setMacrosAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickFuera);
      document.removeEventListener('keydown', onEscape);
    };
  }, [macrosAbierto]);

  // Mismo cierre para el menú del botón Enviar.
  useEffect(() => {
    if (!envioAbierto) return undefined;
    const onClickFuera = (e) => {
      if (envioRef.current && !envioRef.current.contains(e.target)) setEnvioAbierto(false);
    };
    const onEscape = (e) => {
      if (e.key === 'Escape') setEnvioAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickFuera);
      document.removeEventListener('keydown', onEscape);
    };
  }, [envioAbierto]);

  const insertarRespuesta = (cuerpo) => {
    editorRef.current?.insertarTexto(cuerpo);
    setMacrosAbierto(false);
  };

  const agregarArchivos = (elegidos) => {
    const validos = elegidos.filter(adjuntoDentroDeLimite);
    setError(
      validos.length < elegidos.length
        ? 'Algún archivo supera el límite de 3,7 MB y se descartó.'
        : '',
    );
    setArchivos((prev) => [...prev, ...validos]);
  };

  const onPickFiles = (e) => {
    agregarArchivos(Array.from(e.target.files || []));
    if (fileRef.current) fileRef.current.value = '';
  };

  const quitarArchivo = (idx) => setArchivos((prev) => prev.filter((_, i) => i !== idx));

  // Otros estados a los que se puede pasar al enviar (el actual ya es el del botón principal).
  const resolver = estados.find(
    (est) => Number(est.id) === ESTADO_RESUELTO && Number(est.id) !== Number(estadoActualId),
  );
  const opcionesEnvio = estados
    .filter((est) => Number(est.id) !== Number(estadoActualId) && est !== resolver)
    .sort((a, b) => prioridadEnvio(a) - prioridadEnvio(b));

  // Sin estadoNuevo se mantiene el estado actual del ticket.
  const enviar = async (estadoNuevo = estadoActualId) => {
    setEnvioAbierto(false);
    // Las imágenes pegadas en la caja viajan como adjuntos inline (cid) junto a su HTML.
    const { html, imagenes } = editorRef.current.leer();
    const ok = await onSend({
      modo,
      texto,
      estadoId: Number(estadoNuevo),
      archivos,
      html,
      imagenes,
    });
    if (ok) {
      // El backend no borra el borrador al enviar el comentario: lo descartamos
      // aqui. Igualamos ultimoGuardado a '' antes de vaciar el textarea para que
      // el efecto de autoguardado no vuelva a dispararse.
      ultimoGuardado.current = '';
      editorRef.current.limpiar();
      setTexto('');
      setArchivos([]);
      setError('');
      descartarBorrador();
    }
  };

  return (
    <div className="tickets-composer">
      <div className="tickets-composer__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'publica'}
          className={`tickets-composer__tab${modo === 'publica' ? ' is-active' : ''}`}
          onClick={() => setModo('publica')}
        >
          Respuesta pública
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'nota'}
          className={`tickets-composer__tab${modo === 'nota' ? ' is-active' : ''}`}
          onClick={() => setModo('nota')}
        >
          Nota interna
        </button>
      </div>

      {modo === 'publica' && onChangeCc && (
        <TicketCcEditor
          solicitante={solicitante}
          cc={cc}
          onChange={onChangeCc}
          guardando={ccGuardando}
        />
      )}

      <RespuestaEditor
        ref={editorRef}
        nota={modo === 'nota'}
        etiqueta={modo === 'nota' ? 'Nota interna' : 'Respuesta pública'}
        placeholder={
          modo === 'nota'
            ? 'Escribe una nota interna… (puedes pegar imágenes)'
            : 'Escribe una respuesta… (puedes pegar imágenes)'
        }
        onTexto={setTexto}
        onError={setError}
        onArchivos={agregarArchivos}
      />

      {archivos.length > 0 && (
        <ul className="tickets-composer__files">
          {archivos.map((f, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${f.name}-${i}`} className="tickets-adjunto">
              <Paperclip size={13} aria-hidden="true" />
              <span className="tickets-adjunto__nombre">{f.name}</span>
              <span className="tickets-adjunto__size">{tamanoLegible(f.size)}</span>
              <button
                type="button"
                className="tickets-tag__remove"
                aria-label={`Quitar ${f.name}`}
                onClick={() => quitarArchivo(i)}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="tickets-composer__error">{error}</p>}

      <div className="tickets-composer__foot">
        <div className="tickets-composer__tools">
          <button
            type="button"
            className="tickets-btn tickets-btn--ghost"
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            <Paperclip size={16} aria-hidden="true" />
            Adjuntar
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="visually-hidden"
            onChange={onPickFiles}
            aria-hidden="true"
            tabIndex={-1}
          />
          <div className="tickets-composer__macros" ref={macrosRef}>
            <button
              type="button"
              className="tickets-btn tickets-btn--ghost"
              onClick={() => setMacrosAbierto((v) => !v)}
              aria-expanded={macrosAbierto}
              aria-haspopup="listbox"
            >
              <MessageSquare size={16} aria-hidden="true" />
              Respuestas guardadas
            </button>
            {macrosAbierto && (
              <div className="tickets-composer__macros-pop">
                {respuestas.length === 0 ? (
                  <p className="tickets-composer__macros-empty">No hay respuestas guardadas</p>
                ) : (
                  <ul
                    className="tickets-composer__macros-list"
                    role="listbox"
                    aria-label="Respuestas guardadas"
                  >
                    {respuestas.map((r) => (
                      <li key={r.id} role="option" aria-selected={false}>
                        <button
                          type="button"
                          className="tickets-composer__macros-item"
                          onClick={() => insertarRespuesta(r.cuerpo || '')}
                        >
                          {r.titulo}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="tickets-composer__send">
          {modo === 'publica' && resolver && (
            <button
              type="button"
              className="tickets-btn tickets-btn--primary"
              onClick={() => enviar(resolver.id)}
              disabled={enviando}
            >
              <Check size={16} aria-hidden="true" />
              Enviar y resolver
            </button>
          )}
          <div className="tickets-composer__split" ref={envioRef}>
            <button
              type="button"
              className="tickets-btn tickets-btn--primary tickets-composer__split-main"
              onClick={() => enviar()}
              disabled={enviando}
            >
              <Send size={16} aria-hidden="true" />
              {enviando ? 'Enviando…' : 'Enviar'}
            </button>
            {modo === 'publica' && opcionesEnvio.length > 0 && (
              <>
                <button
                  type="button"
                  className="tickets-btn tickets-btn--primary tickets-composer__split-caret"
                  onClick={() => setEnvioAbierto((v) => !v)}
                  disabled={enviando}
                  aria-label="Más opciones de envío"
                  aria-haspopup="menu"
                  aria-expanded={envioAbierto}
                >
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
                {envioAbierto && (
                  <ul className="tickets-composer__split-menu" role="menu">
                    {opcionesEnvio.map((est) => (
                      <li key={est.id} role="none">
                        <button
                          type="button"
                          role="menuitem"
                          className="tickets-composer__split-item"
                          onClick={() => enviar(est.id)}
                        >
                          {etiquetaEnvio(est)}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

TicketComposer.propTypes = {
  estadoActualId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  estados: PropTypes.arrayOf(PropTypes.object),
  onSend: PropTypes.func.isRequired,
  enviando: PropTypes.bool,
  headquarter: PropTypes.string,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  solicitante: PropTypes.string,
  cc: PropTypes.arrayOf(PropTypes.string),
  // Sin onChangeCc no se pinta el bloque Para/CC (p. ej. en tests aislados).
  onChangeCc: PropTypes.func,
  ccGuardando: PropTypes.bool,
};

export default TicketComposer;
